import React from 'react';
import { makeStyles } from '@mui/styles';
import { Tooltip, Typography, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useTooltipSession from '../../hooks/useTooltipSession';

const useStyles = makeStyles(() => ({
  tooltip: {
    background: '#2d2d2d',
    border: 'none',
    borderRadius: 8,
    padding: 0,
    maxWidth: 280,
    minWidth: 260,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 12px 8px 12px',
    borderBottom: 'none',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  title: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    lineHeight: '20px',
  },
  closeButton: {
    padding: '2px',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
    },
  },
  tooltipContent: {
    padding: '0 12px 12px 12px',
  },
  description: {
    fontSize: '12px',
    color: '#ffffff',
    lineHeight: '16px',
    weight: '400',
  },
  arrow: {
    color: '#2d2d2d',
    '&::before': {
      border: 'none',
    },
  },
  popper: ({ zIndex }) => ({
    zIndex,
  }),
  toggleAIActive: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    padding: '2px',
    color: 'white',
    background:
      'linear-gradient(180deg, #735FE3 29.33%, #42B3FF 100%), linear-gradient(360deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.17) 100%)',
  },
  toggleBlocksActive: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    padding: '2px',
    color: 'white',
    backgroundColor: 'var(--color-highlight)',
  },
}));

const AdvancedTooltip = ({
  children,
  title,
  description,
  icon,
  iconClass,
  tooltipId,
  zIndex = 1500,
  open = false,
  onClose,
  ...otherProps
}) => {
  const classes = useStyles({ zIndex });
  const { markTooltipClosed, isTooltipClosed } = useTooltipSession();

  const wasClosed = tooltipId ? isTooltipClosed(tooltipId) : false;

  const shouldShowTooltip = open && !wasClosed;

  const handleCloseClick = (event) => {
    event.stopPropagation();

    if (tooltipId) {
      markTooltipClosed(tooltipId);
    }

    if (onClose) {
      onClose(event);
    }
  };

  const tooltipContent = (
    <Paper className={classes.tooltip} elevation={0}>
      {title && (
        <div className={classes.tooltipHeader}>
          <div className={classes.titleContainer}>
            {icon && (iconClass ? <div className={classes[iconClass]}>{icon}</div> : icon)}
            <Typography className={classes.title}>{title}</Typography>
          </div>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={handleCloseClick}
            aria-label="Cerrar tooltip"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      {description && (
        <div className={classes.tooltipContent}>
          <Typography className={classes.description}>{description}</Typography>
        </div>
      )}
    </Paper>
  );

  return (
    <Tooltip
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.arrow,
        popper: classes.popper,
      }}
      title={tooltipContent}
      open={shouldShowTooltip}
      arrow
      disableHoverListener
      disableFocusListener
      disableTouchListener
      {...otherProps}
    >
      {children}
    </Tooltip>
  );
};

export default AdvancedTooltip;
